import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { HaftpflichtSchadenVorhanden } from '@app/shared/gsm/gsm-ui-lib-elements/src/app/dynamic-form-components/shared-models';
import { GeschaedigtePerson, HaftpflichtSchadenWithAufstellung } from '@gsm/entities';
import { DynamicFormControlEvent, DynamicFormGroupModel, DynamicFormModel, DynamicSelectModel } from '@ng-dynamic-forms/core';
import { DynamicMaterialFormComponent } from '@ng-dynamic-forms/ui-material';
import { WINDOW } from 'ngx-window-token';
import { CustomDynamicFormService } from '../../custom-dynamic-form.service';
import { setModelDisabled } from '../../helpers/disable-model.helper';
import { filterEmptyProps } from '../../helpers/filter-props.helper';

import { formElementsInvalid } from '../../helpers/form-elements-invalid.helper';
import { getGroupValue } from '../../helpers/get-group-value.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { getKontaktOptionsFromValue, HAFTPFLICHT_GESCHAEDIGTE_PERSON_TEMPLATE, KONTAKT_FIELDS } from './haftpflicht-geschaedigte-person.config';
import { HaftpflichtGeschaedigtePersonModel } from './haftpflicht-geschaedigte-person.model';

// NOTE:
// The base class CustomDynamicFormControlComponent already includes all inputs and outputs needed, as well
// as additional convenience methods onDisabledChange() and onValueChange, for when model.disabled and model.value change,
// respectively.

@Component({
  selector:    'gsm-haftpflicht-geschaedigte-person',
  templateUrl: 'haftpflicht-geschaedigte-person.component.html',
  styleUrls:   ['haftpflicht-geschaedigte-person.component.scss'],
  imports:     [
    DynamicMaterialFormComponent
  ],
  standalone:  true
})
export class HaftpflichtGeschaedigtePersonComponent extends CustomDynamicFormControlComponent<HaftpflichtGeschaedigtePersonModel>
  implements OnInit {

  formService = inject(CustomDynamicFormService);
  private window = inject<Window>(WINDOW);

  @ViewChild('form', {static: true}) formComponent: DynamicMaterialFormComponent;

  formModel: DynamicFormModel;
  formGroup: UntypedFormGroup;

  private _isUpdating = false;

  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  protected onModelChange() {
    const json = HAFTPFLICHT_GESCHAEDIGTE_PERSON_TEMPLATE(this.model.value, this.model.additional);

    if (!this.model.value) {
      this.model.value = {};
    }

    if (this.model.disabled) {
      setModelDisabled(json, true);
    }

    this.formModel = this.formService.fromJSON(json);
    this.formGroup = this.formService.createFormGroup(this.formModel);

    this.window.setTimeout(() => {
      this._emitChange();
    }, 0);
  }

  onFormChange(event: DynamicFormControlEvent) {
    if (this._isUpdating) {
      return;
    }
    this._isUpdating = true;

    this.model.value = this._getValueFromForm();

    if (event.model?.id === 'geschaedigtePerson') {
      this.formGroup.markAllAsTouched();
    }

    // If a kontakt field has changed, adjust display of kontakt options.
    if (event.model?.id === 'geschaedigtePerson' || KONTAKT_FIELDS.includes(event.model?.id)) {
      this._configureKontaktOptions();
    }

    this._emitChange();

    this.formService.detectChanges(this.formComponent);

    this._isUpdating = false;
  }

  private _emitChange() {
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors(): ValidationErrors {
    const isValid = !formElementsInvalid(this.formGroup, this.formModel);
    return isValid ? null : {geschaedigtePerson: true};
  }

  private _getValueFromForm(): HaftpflichtSchadenWithAufstellung {
    const {
      id,
      geschaedigtePerson,
      geschaedigtePersonNameErfassen: {
                                        vorname,
                                        nachname
                                      } = {} as any,
      geschaedigtePersonErfassen:     {
                                        plz,
                                        ort,
                                        strasse,
                                        hausNummer,
                                        email,
                                        telephoneNumber
                                      } = {} as any,
      kontaktTyp_group:               {kontaktTyp} = {} as any,
      verhaeltnis_group:              {
                                        verhaeltnis,
                                        verhaeltnisList,
                                        verwandtschaftsverhaeltnis_group: {
                                                                            verwandschaftsVerhaeltnisTyp,
                                                                            anderesVerwandschaftsVerhaeltnis
                                                                          } = {} as any,
                                        wohnverhaeltnis_group:            {wohnVerhaeltnis} = {} as any
                                      } = {} as any
    } = getGroupValue(this.formModel);

    const vorhanden = !geschaedigtePerson
      ? null
      : typeof geschaedigtePerson === 'string'
        ? HaftpflichtSchadenVorhanden.fromString(geschaedigtePerson)
        : HaftpflichtSchadenVorhanden.Vorhanden;

    const adresse = filterEmptyProps({
      plz,
      ort,
      strasse,
      hausNummer
    });

    const person: GeschaedigtePerson =
      vorhanden === HaftpflichtSchadenVorhanden.Vorhanden
        ? geschaedigtePerson
        : vorhanden === HaftpflichtSchadenVorhanden.Erfassen || vorhanden === HaftpflichtSchadenVorhanden.SieheBeilage
          ? {
            vorname,
            nachname,
            adresse: Object.keys(adresse).length ? adresse : null,
            email,
            telephoneNumber
          }
          : null;

    const result = filterEmptyProps<HaftpflichtSchadenWithAufstellung>({
      id,
      vorhanden,
      geschaedigtePerson:
        person &&
        filterEmptyProps({
          ...person,
          kontaktTyp,
          verhaeltnis,
          verhaeltnisList,
          verwandschaftsVerhaeltnisTyp,
          anderesVerwandschaftsVerhaeltnis,
          wohnVerhaeltnis
        })
    });

    return Object.keys(result).length ? result : null;
  }

  private _configureKontaktOptions() {
    const kontaktGroupModel = this.formService.findModelById<DynamicFormGroupModel>('kontaktTyp_group', this.formModel);
    const kontaktModel = this.formService.findModelById<DynamicSelectModel<string>>('kontaktTyp', kontaktGroupModel.group);
    const kontaktOptions = getKontaktOptionsFromValue(this.model.value);

    kontaktGroupModel.hidden = !kontaktOptions.length;
    kontaktModel.options = kontaktOptions;

    // Disable kontakt select if there is only one choice.
    kontaktModel.disabled = kontaktOptions.length === 1;

    // Preselect kontakt option if there is only one choice.
    if (kontaktOptions.length === 1) {
      kontaktModel.value = kontaktOptions[0].value;
    }
  }
}
